/**
 * MovementComponent - Handles entity movement
 */

import { Component } from '../Component';
import { ComponentType } from '../../types/enums';
import { Vector2 } from '../../math/Vector2';
import { Vector3 } from '../../math/Vector3';

export class MovementComponent extends Component {
  readonly type = ComponentType.Movement;

  private speed: number;
  private velocity: Vector2 = new Vector2(0, 0);
  private friction: number;
  private isMoving: boolean = false;
  private facingDirection: Vector2 = new Vector2(0, 1); // Default facing down

  // Collision callback
  public canMoveTo: ((x: number, y: number) => boolean) | null = null;

  constructor(speed: number = 5, friction: number = 10) {
    super();
    this.speed = speed;
    this.friction = friction;
  }

  update(deltaTime: number): void {
    if (!this.owner) return;

    // Apply friction
    this.velocity = this.velocity.multiply(Math.exp(-this.friction * deltaTime));

    // Calculate movement
    const movement = this.velocity.multiply(deltaTime);

    if (movement.magnitude() > 0.001) {
      const newX = this.owner.position.x + movement.x;
      const newY = this.owner.position.y + movement.y;

      // Check collision
      if (this.canMoveTo) {
        // Try full movement
        if (this.canMoveTo(newX, newY)) {
          this.owner.position.x = newX;
          this.owner.position.y = newY;
        }
        // Try X only
        else if (this.canMoveTo(newX, this.owner.position.y)) {
          this.owner.position.x = newX;
        }
        // Try Y only
        else if (this.canMoveTo(this.owner.position.x, newY)) {
          this.owner.position.y = newY;
        }
      } else {
        this.owner.position.x = newX;
        this.owner.position.y = newY;
      }

      this.isMoving = true;
    } else {
      this.isMoving = false;
    }
  }

  /** Apply movement input */
  move(direction: Vector2): void {
    if (direction.magnitude() > 0) {
      const normalizedDir = direction.normalize();
      this.velocity = normalizedDir.multiply(this.speed);
      this.facingDirection = normalizedDir;
    }
  }

  /** Move towards a target position */
  moveTowards(target: Vector3, _deltaTime: number): void {
    if (!this.owner) return;

    const dx = target.x - this.owner.position.x;
    const dy = target.y - this.owner.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 0.1) {
      const direction = new Vector2(dx / dist, dy / dist);
      this.move(direction);
    }
  }

  /** Stop all movement */
  stop(): void {
    this.velocity = new Vector2(0, 0);
  }

  /** Apply impulse (instant velocity change) */
  applyImpulse(impulse: Vector2): void {
    this.velocity = this.velocity.add(impulse);
  }

  /** Apply knockback from a source position */
  knockback(sourcePos: Vector3, force: number): void {
    if (!this.owner) return;

    const dx = this.owner.position.x - sourcePos.x;
    const dy = this.owner.position.y - sourcePos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 0) {
      const knockbackDir = new Vector2(dx / dist, dy / dist);
      this.applyImpulse(knockbackDir.multiply(force));
    }
  }

  getVelocity(): Vector2 {
    return this.velocity.clone();
  }

  getSpeed(): number {
    return this.speed;
  }

  setSpeed(speed: number): void {
    this.speed = speed;
  }

  getIsMoving(): boolean {
    return this.isMoving;
  }

  getFacingDirection(): Vector2 {
    return this.facingDirection.clone();
  }

  setFacingDirection(dir: Vector2): void {
    if (dir.magnitude() > 0) {
      this.facingDirection = dir.normalize();
    }
  }
}
